import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

/**
 * Renders an image that lives behind an authenticated API route (evidence
 * photos, ID docs, shipping labels). A plain <img src> can't carry the bearer
 * token, so we fetch the bytes through the API client, turn them into an object
 * URL, and render that. Clicking opens the full image in a new tab.
 *
 * `path` is an API path like `/orders/:id/evidence/dropoff` - never a public URL.
 */
export function AuthedImage({ path, alt = '', className = '' }: { path: string; alt?: string; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    api.get(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (failed) return <div className={`grid place-items-center bg-cream-deep text-mute text-[10px] ${className}`}>✕</div>;
  if (!url) return <div className={`bg-cream-deep animate-pulse ${className}`} />;
  return (
    <button type="button" onClick={() => window.open(url, '_blank', 'noopener')} className="block" title="Open full size">
      <img src={url} alt={alt} className={className} />
    </button>
  );
}
